import * as api from "./middleware/api.middleware";
import { requireActiveEnvironmentId } from "./environment.store";
import { getAuthHeader } from "./auth/authHeaders";
import {
  IDashboardDataQuality,
  IDashboardDataQualityHistory,
  IDashboardOverview,
} from "../interfaces/dashboard.interfaces";

const serviceUrl = "environments";

const getAccessToken = () => getAuthHeader();

const getEnvironmentId = () => requireActiveEnvironmentId();

export function getDashboardOverview(environmentId: number) {
  const getResponse = api.get<IDashboardOverview>(
    `${serviceUrl}/${environmentId}/dashboard/overview`,
    getAccessToken()
  );
  return getResponse;
}

export function getDashboardDataQuality(environmentId: number) {
  const getResponse = api.get<IDashboardDataQuality>(
    `${serviceUrl}/${environmentId}/dashboard/data_quality`,
    getAccessToken()
  );
  return getResponse;
}

export function getDashboardDataQualityHistory(environmentId?: number) {
  const getResponse = api.get<IDashboardDataQualityHistory>(
    `${serviceUrl}/${environmentId ?? getEnvironmentId()}/dashboard/data_quality/history`,
    getAccessToken()
  );
  return getResponse;
}

export function getRdvDataQuality() {
  const getResponse = api.get<any[]>(
    `${serviceUrl}/${getEnvironmentId()}/data_quality/raw_data_vault`,
    getAccessToken()
  );
  return getResponse;
}

export function getDmDataQuality() {
  const getResponse = api.get<any[]>(
    `${serviceUrl}/${getEnvironmentId()}/data_quality/data_mart`,
    getAccessToken()
  );
  return getResponse;
}

export function getRawDataVaultHistory() {
  const getResponse = api.get<any[]>(
    `${serviceUrl}/${getEnvironmentId()}/data_quality/raw_data_vault/history`,
    getAccessToken()
  );
  return getResponse;
}

export function getDataMartHistory() {
  const getResponse = api.get<any[]>(
    `${serviceUrl}/${getEnvironmentId()}/data_quality/data_mart/history`,
    getAccessToken()
  );
  return getResponse;
}

export type ApiCallSummaryGroupingLevel =
  | "ENVIRONMENT"
  | "LAYER"
  | "JOB_STEP_TYPE"
  | "JOB_EXECUTION"
  | "JOB"
  | "JOB_TASK_GROUP"
  | "JOB_TASK";

export interface ApiCallSummaryFilters {
  grouping_path: ApiCallSummaryGroupingLevel[];
  start_date?: string;
  end_date_exclusive?: string;
  api_calls_per_task_pipeline_run: number;
  consumption_type?: 0 | 1;
}

export interface JobExecutionApiCallSummaryRow {
  grouping_path?: string | null;
  activity_year?: number | null;
  activity_month?: number | null;
  activity_month_start_date?: string | null;
  activity_month_label?: string | null;
  environment_id?: number | null;
  task_pipeline_run_count?: number | null;
  api_calls_per_task_pipeline_run?: number | null;
  estimated_api_call_count?: number | null;
  job_execution_count?: number | null;
  unique_job_count?: number | null;
  unique_job_task_group_count?: number | null;
  unique_job_task_count?: number | null;
  succeeded_count?: number | null;
  first_activity_datetime?: string | null;
  last_activity_datetime?: string | null;
  first_task_start_time?: string | null;
  last_task_end_time?: string | null;
  first_log_created_at?: string | null;
  last_log_updated_at?: string | null;
}

export interface AutomationApiCallSummaryRow {
  environment_id?: number | null;
  activity_year?: number | null;
  activity_month?: number | null;
  activity_month_start_date?: string | null;
  activity_month_label?: string | null;
  total_automation_count?: number | null;
  total_data_vault_automations?: number | null;
  total_data_mart_automations?: number | null;
}

export interface ApiCallSummaryResponse {
  environment_id: number;
  include_monthly_breakdown?: boolean;
  total_records: number;
  data: JobExecutionApiCallSummaryRow[] | AutomationApiCallSummaryRow[];
}

export function getJobExecutionApiCallSummary(
  filters?: ApiCallSummaryFilters,
  environmentId = getEnvironmentId()
) {
  const params = new URLSearchParams();
  if (filters) {
    params.set("grouping_path", filters.grouping_path.join(","));
    params.set(
      "api_calls_per_task_pipeline_run",
      String(filters.api_calls_per_task_pipeline_run)
    );
    if (filters.start_date) params.set("start_date", filters.start_date);
    if (filters.end_date_exclusive) params.set("end_date_exclusive", filters.end_date_exclusive);
    if (filters.consumption_type !== undefined) params.set("consumption_type", String(filters.consumption_type));
  }
  const queryString = params.toString();
  return api.get<ApiCallSummaryResponse>(
    `${serviceUrl}/${environmentId}/job_execution_api_call_summary_v3${queryString ? `?${queryString}` : ""}`,
    getAccessToken()
  );
}
